import React from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import {
  HealthAndSafety,
  Security,
  Code,
  Handyman,
} from '@mui/icons-material';
import type { ImprovementObjectiveType } from './simulatorUtils';

interface ImprovementObjectiveProps {
  selectedObjective: ImprovementObjectiveType;
  onChangeObjective: (objective: ImprovementObjectiveType) => void;
}

interface ObjectiveOption {
  id: ImprovementObjectiveType;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const OBJECTIVE_OPTIONS: ObjectiveOption[] = [
  {
    id: 'overall',
    label: 'Overall Health',
    icon: <HealthAndSafety fontSize="small" />,
    description: 'Prioritize by critical & high severity to boost total health score',
  },
  {
    id: 'security',
    label: 'Security',
    icon: <Security fontSize="small" />,
    description: 'Prioritize vulnerabilities, injection flaws, and security CVEs',
  },
  {
    id: 'quality',
    label: 'Code Quality',
    icon: <Code fontSize="small" />,
    description: 'Prioritize complexity, architecture, and maintainability issues',
  },
  {
    id: 'debt',
    label: 'Technical Debt',
    icon: <Handyman fontSize="small" />,
    description: 'Prioritize high-effort technical debt and outdated dependencies',
  },
];

export const ImprovementObjective: React.FC<ImprovementObjectiveProps> = ({
  selectedObjective,
  onChangeObjective,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: '#334155',
          fontSize: '0.875rem',
          mb: 1.25,
        }}
      >
        What do you want to improve?
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {OBJECTIVE_OPTIONS.map((opt) => {
          const isSelected = selectedObjective === opt.id;
          return (
            <Tooltip key={opt.id} title={opt.description} arrow enterDelay={400}>
              <Button
                variant={isSelected ? 'contained' : 'outlined'}
                onClick={() => onChangeObjective(opt.id)}
                startIcon={opt.icon}
                sx={{
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: isSelected ? 700 : 500,
                  py: 0.75,
                  px: 2,
                  boxShadow: isSelected ? '0 2px 8px rgba(37, 99, 235, 0.25)' : 'none',
                  bgcolor: isSelected ? '#2563eb' : '#ffffff',
                  color: isSelected ? '#ffffff' : '#475569',
                  borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isSelected ? '#1d4ed8' : '#f8fafc',
                    borderColor: isSelected ? '#1d4ed8' : '#94a3b8',
                    color: isSelected ? '#ffffff' : '#0f172a',
                  },
                }}
              >
                {opt.label}
              </Button>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};
